/**
 * bcrypt browser/test mock: replaces the native `bcrypt` binding with a
 * PBKDF2 implementation using the Web Crypto API (available in both
 * browsers and modern Node.js ≥ 15).
 *
 * Format: `pbkdf2$<salt_hex>$<derived_key_hex>`
 * - 16-byte random salt per hash
 * - 100 000 PBKDF2-SHA256 iterations
 * - 32-byte derived key
 *
 * Applied via resolve.alias in vitest.config.ts and vite.config.ts.
 */

const PREFIX = 'pbkdf2';
const ITERATIONS = 100_000;
const KEY_LENGTH = 32; // bytes
const SALT_LENGTH = 16; // bytes

function toHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

function fromHex(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

async function deriveKey(password: string, salt: Uint8Array): Promise<ArrayBuffer> {
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveBits']
  );
  return crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: ITERATIONS },
    keyMaterial,
    KEY_LENGTH * 8
  );
}

export async function hash(data: string, _saltOrRounds: number | string): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  const derived = await deriveKey(String(data), salt);
  return `${PREFIX}$${toHex(salt)}$${toHex(derived)}`;
}

export async function compare(data: string, encrypted: string): Promise<boolean> {
  const parts = encrypted.split('$');
  if (parts.length !== 3 || parts[0] !== PREFIX) return false;
  const salt = fromHex(parts[1]);
  const derived = await deriveKey(String(data), salt);
  return toHex(derived) === parts[2];
}

export async function genSalt(_rounds = 10): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  return `${PREFIX}$${toHex(salt)}`;
}

export default { hash, compare, genSalt };
